import { ScreenWidthType } from 'components/types'
import formName from 'constants/formName'
import { actions, actionTypes, selectors } from 'data'
import { SymbolType } from 'data/types'
import isNil from 'lodash/isNil'
import omit from 'lodash/omit'
import { actionTypes as reduxActionTypes } from 'redux-form'
import { call, put, select, takeEvery } from 'redux-saga/effects'
import webSocket from 'services/api/webSocket'
import { getClOrdID, isCompact, toMaxPrecision } from 'utils'
import { addTiming } from 'utils/datadog'

import { TicketFormType } from './types'

const defaultPairsToLimitOrder = ['BTC-EUR']

const softReset = function* () {
  const symbol: string = yield select(selectors.instrument.getSymbol)
  const baseCurrency: string = yield select(selectors.instrument.getBaseSymbol)
  const counterCurrency: string = yield select(selectors.instrument.getCounterSymbol)
  const values: TicketFormType = yield select(selectors.ticket.getValues)

  const formValues = {
    ...selectors.ticket.initialValues,
    collateralCurrency: values.side === 'buy' ? counterCurrency : baseCurrency,
    goodToDate: values.goodToDate,
    side: values.side,
    tif: values.tif,
    type: webSocket.order.type.MARKET
  }

  if (defaultPairsToLimitOrder.includes(symbol)) {
    formValues.type = webSocket.order.type.LIMIT
  }

  yield put(actions.form.initialize(formName.TICKET, formValues))
}

const formatExpireDate = (date?: Date): string | null => {
  if (isNil(date)) return null

  return (
    date
      // YYYYMMDD format
      .toLocaleDateString('zh-Hans-CN', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      })
      .replace(/\//g, '')
  )
}

const create = function* () {
  const values: TicketFormType = yield select(selectors.ticket.getValues)
  const symbol: SymbolType = yield select(selectors.instrument.getSymbol)
  const screenWidth: ScreenWidthType = yield select(selectors.app.getScreenWidth)

  const order = {
    clOrdID: getClOrdID(isCompact(screenWidth)),
    collateralCurrency: values.marginOrder ? values.collateralCurrency : undefined,
    execInst: isNil(values.liquidity) ? undefined : 'ALO',
    expireDate: formatExpireDate(values.goodToDate),
    leverageRatio: values.marginOrder ? values.leverageRatio : undefined,
    maxOrder: values.maxOrder,
    minQty: values.minimumQuantity,
    ordType: values.type,
    orderQty: parseFloat(values.quantity ?? '0'),
    price: parseFloat(values.limitPrice ?? '0'),
    side: values.side,
    stopLoss: values.marginOrder && values.hasStopLoss ? values.stopLoss : undefined,
    stopPx: values.triggerPrice || 0,
    symbol,
    takeProfit: values.marginOrder && values.hasTakeProfit ? values.takeProfit : undefined,
    timeInForce: values.tif
  }

  yield put(
    actions.order.add({
      clOrdID: order.clOrdID,
      created: new Date().toISOString(),
      cumQty: 0,
      execInst: order.execInst,
      leavesQty: 0,
      leverageRatio: order.leverageRatio,
      marginOrder: values.marginOrder,
      maxOrder: order.maxOrder,
      ordStatus: webSocket.order.status.NEW,
      ordType: order.ordType,
      orderID: '',
      orderQty: order.orderQty,
      price: order.price,
      sendingTime: '',
      side: order.side,
      stopPx: order.stopPx,
      symbol: order.symbol,
      timeInForce: order.timeInForce,
      // transactTime: it is used to sort the list in Trade/Open tabs temporally
      // The real order will be coming from ws with the real transactTime
      transactTime: new Date().toISOString()
    })
  )

  const action = values.marginOrder ? webSocket.action.CREATE_MARGIN : webSocket.action.CREATE
  const channel = webSocket.channel.TRADING
  const buyMarketOrder = omit(order, ['timeInForce', 'minQty'])
  const orderData =
    order.ordType === webSocket.order.type.MARKET && order.side === 'buy' ? buyMarketOrder : order

  localStorage.setItem('last-order-placed', order.clOrdID)
  addTiming('place-order-time-start')

  yield put(
    // @ts-ignore FIXME: theres an issue with the
    actions.socket.send({
      action,
      channel,
      ...orderData
    })
  )

  // FEEX-141 reset all fields but side
  const resetValues = {
    ...selectors.ticket.initialValues,
    side: order.side
  }
  yield put(actions.form.initialize(formName.TICKET, resetValues))
}

const updatePrecision = function* (
  value: string | number | undefined,
  name: string,
  precision: number
) {
  if (isNil(value)) return
  const valueAsNumber = typeof value === 'string' ? parseFloat(value ?? '0') : value ?? 0
  yield put(actions.form.change(formName.TICKET, name, toMaxPrecision(valueAsNumber, precision)))
}

const updateDecimalPrecisions = function* () {
  const { amount, price } = yield select(selectors.instrument.getPrecision)
  const { limitPrice, minimumQuantity, quantity, stopPrice, triggerPrice }: TicketFormType =
    yield select(selectors.ticket.getValues)
  yield call(updatePrecision, quantity, 'quantity', amount)
  yield call(updatePrecision, minimumQuantity, 'minimumQuantity', amount)
  yield call(updatePrecision, limitPrice, 'limitPrice', price)
  yield call(updatePrecision, stopPrice, 'stopPrice', price)
  yield call(updatePrecision, triggerPrice, 'triggerPrice', price)
}

const ticketChangesWatcher = function* () {
  const {
    collateralCurrency,
    leverageRatio,
    limitPrice,
    marginOrder,
    quantity,
    side
  }: TicketFormType = yield select(selectors.ticket.getValues)
  const symbol: SymbolType = yield select(selectors.instrument.getSymbol)

  if (!marginOrder || !quantity) {
    return
  }

  yield put(
    actions.order.fetchMarginOrderDetails({
      amount: quantity,
      collateralCurrency,
      leverageRatio,
      limitPrice: limitPrice ?? '0',
      side,
      symbol
    })
  )

  yield put(actions.order.setMarginDetails())
}

const isTicketChangeAction = (action) =>
  action.type === reduxActionTypes.CHANGE && action.meta.form === formName.TICKET

export default function* orderTicket() {
  yield takeEvery(actionTypes.ticket.CREATE, create)
  yield takeEvery(actionTypes.ticket.RESET, softReset)
  yield takeEvery(actionTypes.instrument.SET_SYMBOL, softReset)
  yield takeEvery(actionTypes.ticket.UPDATE_DECIMAL_PRECISIONS, updateDecimalPrecisions)
  yield takeEvery(isTicketChangeAction, ticketChangesWatcher)
}
